import { responsiveMediaAbove } from '../../settings/responsive';
import {
  CLASS_ACTIVE,
  CLASS_HIDDEN,
  CLASS_MENU_TOGGLE,
  CLASS_SCROLL_DISABLED,
  CLASS_SUBMENU_OPEN,
  CLASS_SUBSUBMENU_OPEN,
  CLASS_ALT,
} from '../../tools/constants/classes';
import { delegate } from '../../tools/utilities/event-tools';

// Selectors
const SELECTOR_SEARCH_TOGGLE = '[data-search-toggle]';
const SELECTOR_SEARCH_INPUT = '[data-search-input]';
const SELECTOR_SEARCH_FORM = '[data-search-form]';
const SELECTOR_ACTIONS_CONTAINER = '[data-actions-container]';
const SELECTOR_MOBILE_MENU_TRIGGER = '[data-mobile-menu-trigger]';
const SELECTOR_SUBMENU_CONTAINER = '[data-submenu-container]';
const SELECTOR_SUBSUBMENU_CONTAINER = '[data-subsubmenu-container]';
const SELECTOR_NESTED_CONTAINER = '[data-nested-container]';
const SELECTOR_HEADER = '[data-header]';

// Functions
const open = (clazz: string) => {
  // Add body classes
  document.body.classList.add(clazz);

  // Disable scroll
  document.body.classList.add(CLASS_SCROLL_DISABLED);
};

const close = (clazz: string) => {
  // Remove body classes
  document.body.classList.remove(clazz);

  // Enable scroll
  document.body.classList.remove(CLASS_SCROLL_DISABLED);
};

/**
 * Handles the functionality of toggling the desktop search form
 * @param evt The mouse click event for when the user clicks the toggle
 */
const onDesktopSearchToggle = (evt: Event) => {
  const searchForm = document.querySelector<HTMLElement>(
    `${SELECTOR_SEARCH_FORM}.is-desktop`
  );
  if (!searchForm) return;

  const searchInput = searchForm.querySelector<HTMLInputElement>(
    SELECTOR_SEARCH_INPUT
  );
  if (!searchInput) return;

  const actionsContainer = document.querySelector<HTMLElement>(
    SELECTOR_ACTIONS_CONTAINER
  );
  if (!actionsContainer) return;

  evt.preventDefault();

  if (searchForm.classList.contains(CLASS_ACTIVE)) {
    searchInput.blur();

    if (!responsiveMediaAbove('XXLARGE')) {
      actionsContainer.classList.remove(CLASS_HIDDEN);
      closeSearch?.classList.remove('active');
    }
  } else {
    searchInput.focus();
    searchInput.select();

    // only hide action items below 2000px
    if (!responsiveMediaAbove('XXLARGE')) {
      actionsContainer.classList.add(CLASS_HIDDEN);
      closeSearch?.classList.add('active');
    }
  }

  searchForm.classList.toggle(CLASS_ACTIVE);
};

// The search toggle functionality
const searchToggles = document.querySelectorAll<HTMLAnchorElement>(
  SELECTOR_SEARCH_TOGGLE
);
searchToggles.forEach((searchToggle) => {
  searchToggle.addEventListener('click', onDesktopSearchToggle);
});

// Close input form on blur
const searchInput = document.querySelector<HTMLInputElement>(
  SELECTOR_SEARCH_INPUT
);
searchInput?.addEventListener('blur', (evt: Event) => {
  const element = evt.target as HTMLInputElement;
  const value = element.value;
  // if search term is not blank, don't toggle the form so that the search icon will be clickable
  if (value) return;
  onDesktopSearchToggle(evt);
});

const menuToggle = (evt: Event) => {
  evt.preventDefault();
  evt.stopPropagation();

  if (document.body.classList.contains(CLASS_MENU_TOGGLE)) {
    close(CLASS_MENU_TOGGLE);
    return;
  }

  open(CLASS_MENU_TOGGLE);

  // close submenu drawer when clicking on close button
  const submenuContainer = document.querySelector<HTMLElement>(
    SELECTOR_SUBMENU_CONTAINER
  );
  if (submenuContainer?.classList.contains(CLASS_SUBMENU_OPEN)) {
    submenuContainer?.classList.remove(CLASS_SUBMENU_OPEN);
  }

  // close subsubmenu drawer when clicking on close button
  const subsubmenuContainer = document.querySelector<HTMLElement>(
    SELECTOR_SUBSUBMENU_CONTAINER
  );
  if (subsubmenuContainer?.classList.contains(CLASS_SUBSUBMENU_OPEN)) {
    subsubmenuContainer?.classList.remove(CLASS_SUBSUBMENU_OPEN);
  }
};

// On mobile menu trigger click
delegate({
  element: document.documentElement,
  selector: SELECTOR_MOBILE_MENU_TRIGGER,
  event: 'click',
  callback: menuToggle,
});

// Hide mega menu on scroll
window.addEventListener('scroll', function () {
  const nestedContainer = document.querySelector<HTMLElement>(
    SELECTOR_NESTED_CONTAINER
  );
  const header = document.querySelector<HTMLElement>(SELECTOR_HEADER);
  let scrollPos = 0;

  const windowY = window.scrollY;
  if (windowY > scrollPos) {
    // Scrolling up
    if (nestedContainer?.classList.contains(CLASS_SUBMENU_OPEN)) {
      nestedContainer?.classList.remove(CLASS_SUBMENU_OPEN);
    }

    // Remove header class
    if (header?.classList.contains(CLASS_ALT)) {
      header?.classList.remove(CLASS_ALT);
    }
    scrollPos = windowY;
  }
});

// Update currency selector in header
const currencyForms = document.querySelectorAll<HTMLFormElement>(
  '[data-region-select-currency]'
);
const currentCurrencyEl = document.querySelector<HTMLFormElement>(
  '[data-current-currency]'
);
const currentCurrencyValue = currentCurrencyEl?.getAttribute(
  'data-current-currency'
);

currencyForms?.forEach((currencyForm: any) => {
  const currencySelector = currencyForm.querySelector('select');
  currencySelector.addEventListener('change', () => {
    currencyForm.submit();
  });

  const arrayOfCurrencies = Array.from(currencySelector.options).map(
    (option: any) => option.value
  );
  if (arrayOfCurrencies.includes(currentCurrencyValue)) {
    currencySelector.value = currentCurrencyValue;
  }
});

// Promo banner
const currentDate = new Date().toLocaleString('en-US', {
  hour12: false,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

const promoEl = document.querySelectorAll<HTMLDivElement>('[data-promo]');
promoEl.forEach((promo: any) => {
  const startData = promo.getAttribute('data-date-start');
  const endData = promo.getAttribute('data-date-end');

  if (currentDate > startData && currentDate < endData) {
    promo.classList.add('is-active');
  } else {
    promo.classList.remove('is-active');
  }
});

// Close search form
const closeSearch = document.querySelector<HTMLDivElement>(
  '[data-close-search]'
);
const searchFormContainer = document.querySelector<HTMLFormElement>(
  '[data-search-form].is-desktop'
);
const actionsContainer = document.querySelector<HTMLFormElement>(
  '[data-actions-container]'
);
closeSearch?.addEventListener('click', (evt: Event) => {
  evt.preventDefault();
  actionsContainer?.classList.remove('is-hidden');
  searchFormContainer?.classList.remove('is-active');
  closeSearch?.classList.remove('active');
});
